import React, { useEffect, useMemo, useRef } from 'react';
import { Form as AntdForm, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../common';
import { useGetPhdStudySubjectsQuery } from '../../../../../redux/services/phd';

const PhdIndividualPlanSubjectsSection = ({
  students = [],
  value = [],
  onChange,
  readOnly = false,
  preloadFromThesis = false,
}) => {
  const { t } = useTranslation();
  const selectedStudentId = AntdForm.useWatch('phd_student_id');
  const selectedSchoolYearId = AntdForm.useWatch('phd_school_year_id');
  const { data: studySubjects, isFetching } = useGetPhdStudySubjectsQuery();
  const previousStudyContext = useRef(undefined);

  const selectedThesis = useMemo(() => {
    const student = students.find(
      (row) => Number(row.id) === Number(selectedStudentId),
    );
    const matchingTheses = (student?.theses || []).filter(
      (row) => row.is_current === true,
    );

    return matchingTheses.length === 1 ? matchingTheses[0] : null;
  }, [selectedStudentId, students]);
  const studyFieldId = selectedThesis?.phd_study_field_id
    ? Number(selectedThesis.phd_study_field_id)
    : null;

  useEffect(() => {
    if (!selectedStudentId || !selectedSchoolYearId) {
      return;
    }

    const studyContext = `${selectedStudentId ?? ''}:${selectedSchoolYearId ?? ''}`;
    const isInitialContext = previousStudyContext.current === undefined;
    if (isInitialContext && !preloadFromThesis) {
      previousStudyContext.current = studyContext;
      return;
    }

    if (previousStudyContext.current === studyContext) {
      return;
    }

    previousStudyContext.current = studyContext;
    onChange(
      selectedThesis
        ? selectedThesis.elective_study_subjects.map((subject) => subject.id)
        : [],
    );
  }, [
    onChange,
    preloadFromThesis,
    selectedSchoolYearId,
    selectedStudentId,
    selectedThesis,
  ]);

  const fieldSubjects = useMemo(
    () =>
      (studySubjects || []).filter((subject) =>
        (subject.fields || []).some(
          (field) => Number(field.id) === studyFieldId,
        ),
      ),
    [studyFieldId, studySubjects],
  );

  const completionTypeLabel = (completionType) => {
    const labels = {
      S: t('Skúška'),
      T: t('Štátna skúška'),
      V: t('Absolvovanie'),
    };

    return labels[completionType] || completionType;
  };
  const electiveSubjectLabel = (subject) =>
    `${subject.name} · ${completionTypeLabel(subject.completion_type)}`;

  const requiredSubjects = useMemo(
    () => fieldSubjects.filter((subject) => subject.completion_type === 'T'),
    [fieldSubjects],
  );
  const electiveOptions = useMemo(
    () =>
      fieldSubjects
        .filter((subject) => ['S', 'V'].includes(subject.completion_type))
        .map((subject) => ({
          value: subject.id,
          label: electiveSubjectLabel(subject),
        })),
    [fieldSubjects, t],
  );

  useEffect(() => {
    if (isFetching || !studyFieldId) {
      return;
    }

    const validIds = new Set(electiveOptions.map((option) => option.value));
    const nextValue = value.filter((id) => validIds.has(id));
    if (nextValue.length !== value.length) {
      onChange(nextValue);
    }
  }, [electiveOptions, isFetching, onChange, studyFieldId, value]);

  if (isFetching) {
    return (
      <CollapseBlock collapsible title={t('Študijné jednotky')}>
        <Spinner />
      </CollapseBlock>
    );
  }

  return (
    <CollapseBlock collapsible title={t('Študijné jednotky')}>
      <div style={{ marginBottom: 16 }}>
        <strong>{t('Povinné študijné jednotky')}</strong>
        <div style={{ marginTop: 8 }}>
          {requiredSubjects.length
            ? requiredSubjects.map((subject) => (
                <Tag key={subject.id}>{subject.name}</Tag>
              ))
            : '—'}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <strong>{t('Výberové študijné jednotky')}</strong>
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          options={electiveOptions}
          value={value}
          onChange={onChange}
          placeholder={t('Vyberte predmety')}
          disabled={readOnly || !studyFieldId}
          style={{ width: '100%', marginTop: 8 }}
        />
      </div>
    </CollapseBlock>
  );
};

export default PhdIndividualPlanSubjectsSection;
